import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import axiosInstance from '../../utils/axios';
import API_URLS from '../../config/api';

function formatDate(value) {
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not available'
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function getCityPreferences(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return value.trim() ? [value] : [];
    }
  }
  return [];
}

function RiderProfiles() {
  const [riders, setRiders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRiders = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axiosInstance.get(API_URLS.admin.riders);
      setRiders(response.data);
    } catch (err) {
      console.error('Error fetching rider profiles:', err);
      setError(err.response?.data?.message || 'Failed to load rider profiles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRiders();
  }, []);

  const filteredRiders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return riders;

    return riders.filter((rider) => [
      rider.firstName,
      rider.lastName,
      rider.email,
      rider.phone,
      rider.preferredPickupLocations,
      ...getCityPreferences(rider.cityPreferences)
    ].some((value) => value?.toLowerCase().includes(query)));
  }, [riders, searchTerm]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Rider Profiles</h1>
        <p className="mt-2 text-sm text-gray-600">
          Rider contact information and saved pickup and city preferences.
        </p>
      </div>

      <div className="mb-6 rounded-lg bg-white p-4 shadow">
        <label htmlFor="rider-search" className="sr-only">Search riders</label>
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            id="rider-search"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by name, contact, or preference"
            className="block w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center" role="status" aria-label="Loading rider profiles">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-indigo-600" />
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          <p>{error}</p>
          <button
            type="button"
            onClick={fetchRiders}
            className="mt-3 rounded bg-red-700 px-3 py-1.5 font-medium text-white hover:bg-red-800"
          >
            Try Again
          </button>
        </div>
      ) : filteredRiders.length === 0 ? (
        <div className="rounded-lg bg-white px-6 py-12 text-center shadow">
          <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h2 className="mt-3 text-sm font-semibold text-gray-900">
            {riders.length ? 'No riders match your search' : 'No rider profiles found'}
          </h2>
          {riders.length > 0 && <p className="mt-1 text-sm text-gray-500">Try another name, contact, or preference.</p>}
        </div>
      ) : (
        <div className="space-y-5">
          <p className="text-sm text-gray-500">{filteredRiders.length} rider{filteredRiders.length === 1 ? '' : 's'}</p>
          {filteredRiders.map((rider) => {
            const cities = getCityPreferences(rider.cityPreferences);
            const status = rider.status || 'active';
            return (
              <article key={rider.id} className="overflow-hidden rounded-lg bg-white shadow">
                <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 px-5 py-5 sm:px-6">
                  <div className="flex items-center gap-3">
                    <UserCircleIcon className="h-12 w-12 flex-shrink-0 text-purple-500" />
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {rider.firstName} {rider.lastName}
                      </h2>
                      <p className="text-sm text-gray-500">Joined {formatDate(rider.createdAt)}</p>
                    </div>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                    status === 'suspended'
                      ? 'bg-red-100 text-red-800'
                      : status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800'
                  }`}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </span>
                </div>

                <div className="grid gap-6 px-5 py-5 sm:grid-cols-2 sm:px-6">
                  <section aria-label="Rider contact information">
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">Contact</h3>
                    <dl className="space-y-3 text-sm">
                      <div className="flex items-center gap-2">
                        <EnvelopeIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        <a className="break-all text-indigo-700 hover:underline" href={`mailto:${rider.email}`}>{rider.email || 'Not provided'}</a>
                      </div>
                      <div className="flex items-center gap-2">
                        <PhoneIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        <a className="text-indigo-700 hover:underline" href={`tel:${rider.phone}`}>{rider.phone || 'Not provided'}</a>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <CalendarIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                        <span>Registered {formatDate(rider.createdAt)}</span>
                      </div>
                    </dl>
                  </section>

                  <section aria-label="Rider preferences">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
                      <MapPinIcon className="h-4 w-4" /> Preferences
                    </h3>
                    <div className="space-y-4 text-sm">
                      <div>
                        <h4 className="font-medium text-gray-700">Preferred pickup locations</h4>
                        <p className="mt-1 whitespace-pre-wrap text-gray-600">
                          {rider.preferredPickupLocations || 'No preferred pickup locations saved.'}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-700">City preferences</h4>
                        {cities.length ? (
                          <ul className="mt-1 flex flex-wrap gap-2">
                            {cities.map((city) => (
                              <li key={city} className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700">
                                {city}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-1 text-gray-600">No city preferences saved.</p>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <section className="mt-8 rounded-lg border border-dashed border-indigo-300 bg-indigo-50/60 p-5 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <span className="inline-flex w-fit rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-800">
            Coming soon
          </span>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Planned rider profile features</h2>
            <p className="mt-1 text-sm text-gray-600">Future updates are planned to include:</p>
            <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-gray-700">
              <li>Booking history.</li>
              <li>Approve/suspend/delete actions, and edit capabilities.</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default RiderProfiles;
